import React from 'react'
import { Button } from '@/components/ui/Button'
import ChannelBadges from './ChannelBadges'
import type { OrgNodeWithChildren } from './OrgTree'
import type { OrgNodeData } from './NodeEditPanel'

// Intentional exception to the no-hex rule: bespoke depth accents, not themeable tokens.
const DEPTH_ACCENT = [
  '#1F2329', // root — graphite ink
  '#B3573A', // L1 — sienna
  '#4E6B4A', // L2 — moss
  '#A68432', // L3 — ochre
  '#5B5470', // L4+ — slate-plum
]
const accentFor = (d: number) => DEPTH_ACCENT[Math.min(d, DEPTH_ACCENT.length - 1)]

interface Props {
  node: OrgNodeWithChildren
  /** 0-based render depth (server `node.depth` is 1-based). */
  depth: number
  siblingIndex: number
  siblingCount: number
  canManage: boolean
  salesUnflaggedIds: Set<string>
  onAddChild: (parent: OrgNodeData) => void
  onEdit: (node: OrgNodeData) => void
  onMove: (node: OrgNodeData, direction: 'left' | 'right') => void
}

export default function OrgTreeRow({ node, depth, siblingIndex, siblingCount, canManage, salesUnflaggedIds, onAddChild, onEdit, onMove }: Props) {
  const canMoveUp = siblingIndex > 0
  const canMoveDown = siblingIndex < siblingCount - 1
  return (
    <div className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent">
      <span
        className="h-4 w-[3px] flex-shrink-0 rounded-full"
        style={{ background: accentFor(depth) }}
      />
      <span className="flex min-w-0 flex-1 items-baseline gap-2">
        <span className="truncate text-sm font-medium text-foreground" title={node.name}>
          {node.name}
        </span>
        <span className="truncate text-xs text-muted-foreground" title={node.slug}>
          {node.slug}
          {node.currency && <span title="Country currency"> · {node.currency}</span>}
        </span>
      </span>
      <span className="flex-shrink-0">
        <ChannelBadges node={node} salesUnflagged={salesUnflaggedIds.has(node.id)} />
      </span>
      {canManage && (
        <div className="flex flex-shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto pointer-events-none">
          {/* Backend move endpoint keeps left/right naming for sibling order; up = left, down = right. */}
          {/* Always rendered (invisible for only children) so badges align across rows. */}
          <span className={siblingCount > 1 ? 'contents' : 'invisible flex gap-0.5'}>
            <IconBtn label="Move up" disabled={!canMoveUp} onClick={() => onMove(node, 'left')}>
              <svg width="11" height="11" viewBox="0 0 11 11"><path d="M1.5 7 L5.5 3 L9.5 7" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconBtn>
            <IconBtn label="Move down" disabled={!canMoveDown} onClick={() => onMove(node, 'right')}>
              <svg width="11" height="11" viewBox="0 0 11 11"><path d="M1.5 4 L5.5 8 L9.5 4" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconBtn>
          </span>
          <IconBtn label="Add child" onClick={() => onAddChild(node)}>
            <svg width="11" height="11" viewBox="0 0 11 11"><path d="M5.5 1v9 M1 5.5h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
          </IconBtn>
          <IconBtn label="Edit" onClick={() => onEdit(node)}>
            <svg width="11" height="11" viewBox="0 0 11 11"><path d="M1.5 9.5 L2 7.5 L7 2.5 L9 4.5 L4 9.5 Z" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinejoin="round" /></svg>
          </IconBtn>
        </div>
      )}
    </div>
  )
}

function IconBtn({ children, label, onClick, disabled = false }: { children: React.ReactNode; label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick() }}
      title={label}
      className="h-5 w-5 text-muted-foreground hover:text-foreground disabled:opacity-30"
    >
      {children}
    </Button>
  )
}
